import { useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useArticleEdit } from '@/module/Author/Article/model/ArticleQuery';
import { ArticleSchema, articleSchema, defaultArticleValues } from '@/module/Author/Article/model/ArticleSchema';
import { defaultIdentifiers } from '@/module/Author/Publication/model/PublicationTypes';

import { useAppNotification } from '@feature/utils';

import { ArticleForm } from './ArticleForm';
import { Article } from './model/ArticleTypes';

interface ArticleEditProps {
  article: Article;
}

const convert = (article: Article): ArticleSchema => ({
  ...article,
  identifiers: article.identifiers ?? defaultIdentifiers,
});

export function ArticleEdit(props: ArticleEditProps) {
  const { article } = props;
  const { t } = useTranslation();
  const { mutate, isPending, isError, isSuccess } = useArticleEdit(article.id);
  const notification = useAppNotification();

  const onSubmit = (values: ArticleSchema) => {
    mutate(values, {
      onError: notification.error,
      onSuccess: notification.success,
    });
  };

  const values = useMemo(() => convert(article), [article]);
  const navigate = useNavigate();

  return (
    <ArticleForm
      title={t('articles.form.title.edit')}
      schema={articleSchema}
      defaultValues={defaultArticleValues}
      values={values}
      onSubmit={onSubmit}
      isError={isError}
      isSuccess={isSuccess}
      isSubmitting={isPending}
      onCancel={() => navigate({ to: '..' })}
      showWosFields={false}
      showScopusFields={article.source.type === 'SCOPUS'}
    />
  );
}
